import { useState, useCallback } from "react";
import { Search, Loader2, AlertCircle, MapPin, Check } from "lucide-react";
import * as search from "@/api/search";
import * as analytics from "@/api/analytics";
import { SEARCH_CATEGORIES } from "@/api/search";
import type { SearchResult, SearchCounts } from "@/api/search";

// ═══════════════════════════════════════════════════════════════════
// The search lab.
//
// This is the first surface from which search can be exercised by hand at all.
// It was built alongside the endpoint rather than after it, because the last
// time a whole feature shipped without one — the Bazaar — everything on top of
// it was invisible for weeks while the data underneath looked fine.
//
// Two things here are deliberate and easy to undo by accident:
//
//   • It does NOT re-sort results. They arrive in the order two ways of
//     matching agreed on, and re-sorting by kind client-side would throw away
//     the only thing that makes one list out of fourteen sorts of content.
//
//   • It shows the COUNTS the server sent, including zeros. Those describe
//     what survived the visibility checks, not what the index matched, and
//     seeing a zero next to a kind is how you notice a rule firing.
//
// The example queries below are not decoration. Each one is there to show a
// behaviour that is hard to see otherwise — a typo, a shelf, a request phrased
// as a need rather than as a trade.
// ═══════════════════════════════════════════════════════════════════

const EXAMPLES: { q: string; why: string }[] = [
  { q: "payasm", why: "a typo — the right sweet still comes back" },
  { q: "kaju katli", why: "a shelf: two shops, two boxes" },
  { q: "somebody to help with my taxes", why: "no shared words with 'accountant'" },
  { q: "who can cook for a party", why: "a need, not a trade" },
  { q: "garba", why: "crosses events, heritage and a beauty package" },
  { q: "xyzzy", why: "MUST return nothing" },
];

/** Kind → how it reads on screen. Unknown kinds fall through to the raw value. */
const LABELS: Record<string, string> = {
  provider: "business",
  provider_item: "on a shelf",
  event: "event",
  question: "question",
  voice: "voice",
  user: "member",
  institution: "institution",
  haat: "haat",
  event_organizer: "organiser",
  event_organizer_prospect: "organiser · claimable",
  nostalgia_board: "memory board",
  dharohar_personality: "heritage · person",
  dharohar_art_form: "heritage · art form",
  dharohar_handicraft: "heritage · craft",
};

export default function SearchIndex() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [city, setCity] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [counts, setCounts] = useState<SearchCounts | null>(null);
  const [ms, setMs] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The id this search was written down under, and which result was reported
  // against it. Both reset on every new search — a click belongs to the search
  // that produced the list, never to the one before it.
  const [searchId, setSearchId] = useState<string | null>(null);
  const [clicked, setClicked] = useState<number | null>(null);
  // Words the corpus has never seen. The only way a full page of near
  // neighbours can admit it did not answer the question.
  const [unmatched, setUnmatched] = useState<string[]>([]);

  const run = useCallback(
    async (override?: string) => {
      const query = (override ?? q).trim();
      if (query.length < 2) {
        setError("Two characters at least.");
        return;
      }
      setQ(query);
      setLoading(true);
      setError(null);
      setClicked(null);
      const started = Date.now();
      try {
        const res = await search.search({
          q: query,
          category,
          city: city || undefined,
          limit: 20,
        });
        setResults(res.data.results);
        setCounts(res.data.counts);
        setSearchId(res.data.searchId);
        setUnmatched(res.data.unmatchedTerms ?? []);
        setMs(Date.now() - started);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Search failed");
        setResults(null);
        setCounts(null);
        setSearchId(null);
        setUnmatched([]);
      } finally {
        setLoading(false);
      }
    },
    [q, category, city],
  );

  /**
   * Say which result was taken, and where it sat.
   *
   * The position is the point. A list whose ninth entry is always the one
   * people open is a list ranked wrongly, and that is a fact no amount of
   * reading the ranking code will produce.
   *
   * Failures are swallowed on purpose. This is bookkeeping about a result the
   * member already has; an error here must never be shown to them as if their
   * click had not worked.
   */
  const reportClick = useCallback(
    (r: SearchResult, index: number) => {
      setClicked(index);
      if (!searchId) return;
      void analytics
        .trackSearchClick({
          searchQueryId: searchId,
          clickedType: r.type,
          clickedId: r.id,
          clickPosition: index + 1,
        })
        .catch(() => {});
    },
    [searchId],
  );

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Search className="w-6 h-6" /> Search
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          One index over fourteen kinds of content. Matched by spelling and by
          meaning at the same time, then ranked by what the two agree on.
        </p>
      </header>

      {/* ── the box ─────────────────────────────────────────────── */}
      <div className="space-y-3 border rounded-lg p-4">
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="bindi, kaju katli, somebody to help with my taxes…"
            className="flex-1 border rounded px-3 py-2"
          />
          <button
            onClick={() => run()}
            disabled={loading}
            className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </button>
        </div>

        <div className="flex gap-2 flex-wrap items-center text-sm">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="border rounded px-2 py-1"
          >
            {SEARCH_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <span className="flex items-center gap-1 text-gray-500">
            <MapPin className="w-3.5 h-3.5" />
          </span>
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="city — widens to the whole metro"
            className="border rounded px-2 py-1 w-64"
          />
        </div>

        <div className="flex flex-wrap gap-1.5 pt-1">
          {EXAMPLES.map((ex) => (
            <button
              key={ex.q}
              onClick={() => run(ex.q)}
              title={ex.why}
              className="text-xs px-2 py-1 rounded border hover:bg-gray-50"
            >
              {ex.q}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* ── the part we could not answer ─────────────────────────── */}
      {unmatched.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded p-3 text-sm">
          <span className="font-medium">
            Nothing here matches {unmatched.map((t) => `"${t}"`).join(", ")}.
          </span>{" "}
          <span className="text-gray-700">
            Not one document contains{" "}
            {unmatched.length === 1 ? "that word" : "those words"}. The results
            below are the nearest things we have — they are shown, not hidden,
            because an approximate answer beats a blank page. This is also the
            recruitment signal: it is recorded, counted, and it is what tells us
            which trade to go and find.
          </span>
        </div>
      )}

      {/* ── what survived, per kind ──────────────────────────────── */}
      {counts && (
        <div className="text-sm">
          <div className="text-gray-500 mb-1">
            {counts.total} results{ms !== null && ` · ${ms}ms`} — counts are what
            survived the visibility checks, not what the index matched
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(counts)
              .filter(([k]) => k !== "total")
              .map(([k, v]) => (
                <span
                  key={k}
                  className={`text-xs px-2 py-0.5 rounded border ${
                    v > 0 ? "bg-gray-100" : "text-gray-400"
                  }`}
                >
                  {k} {v}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* ── the results, in the order the server chose ───────────── */}
      {results && results.length === 0 && (
        <div className="text-sm text-gray-500 border rounded p-4">
          Nothing matched. That is a real answer — a search that always finds
          something cannot be trusted when it does.
        </div>
      )}

      {results && results.length > 0 && (
        <>
          <div className="text-xs text-gray-500">
            Click a result to record which one was taken and where it sat.
            That is what makes ranking measurable — and this Lab is currently
            the only place it happens from.
          </div>
          <ol className="space-y-2">
            {results.map((r, i) => (
            <li
              key={`${r.type}:${r.id}`}
              onClick={() => reportClick(r, i)}
              className={`border rounded p-3 flex gap-3 cursor-pointer hover:bg-gray-50 ${
                clicked === i ? "ring-1 ring-black" : ""
              }`}
            >
              <span className="text-xs text-gray-400 w-6 pt-1">{i + 1}</span>
              {r.imageUrl && (
                <img
                  src={r.imageUrl}
                  alt=""
                  className="w-12 h-12 object-cover rounded shrink-0"
                />
              )}
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-medium">{r.title}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                    {LABELS[r.type] ?? r.type}
                  </span>
                  {r.matchedBy && !r.matchedBy.includes("spelling") && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-800"
                      title="Found by meaning alone: the nearest neighbour, not a match for the words typed"
                    >
                      related
                    </span>
                  )}
                </div>
                {r.subtitle && (
                  <div className="text-sm text-gray-600">{r.subtitle}</div>
                )}
                {r.description && (
                  <div className="text-sm text-gray-500 mt-0.5">{r.description}</div>
                )}
                <div className="text-xs text-gray-400 mt-1">{r.url}</div>
                {clicked === i && (
                  <div className="text-xs text-green-700 mt-1 flex items-center gap-1">
                    <Check className="w-3 h-3" /> recorded at position {i + 1}
                  </div>
                )}
              </div>
            </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
